import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Activity, AlertTriangle, RefreshCw, Download, Cpu, MemoryStick, HardDrive, Network, Users, Clock, Bell, Search } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Gauge = ({ value, label, icon: Icon, accent = 'indigo', dangerAt = 90 }) => {
  const safe = Math.min(100, Math.max(0, Number(value) || 0));
  const danger = safe >= dangerAt;
  const color = danger ? 'bg-red-500' : safe >= 70 ? 'bg-amber-500' : `bg-${accent}-500`;
  const text = danger ? 'text-red-700' : safe >= 70 ? 'text-amber-700' : `text-${accent}-700`;
  return (
    <div className="rounded-lg border border-gray-200 p-4 bg-white" data-testid={`gauge-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          {Icon && <Icon className="w-4 h-4" />}
          <span>{label}</span>
        </div>
        <span className={`font-semibold ${text}`}>{safe.toFixed(1)}%</span>
      </div>
      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${safe}%` }} />
      </div>
    </div>
  );
};

const StatCard = ({ label, value, sub, accent = 'slate', icon: Icon, danger = false }) => (
  <div className={`rounded-lg p-4 border ${danger ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}>
    <div className="flex items-center justify-between text-xs text-gray-500 uppercase tracking-wide">
      <span>{label}</span>
      {Icon && <Icon className="w-4 h-4" />}
    </div>
    <div className={`text-2xl font-bold mt-1 ${danger ? 'text-red-700' : `text-${accent}-900`}`}>{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
  </div>
);

const APMPortal = () => {
  // ---------- metrics ----------
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchMetrics = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/apm/metrics`, { headers: { Authorization: `Bearer ${token}` } });
      setMetrics(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load metrics');
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const t = setInterval(fetchMetrics, 30000);
    return () => clearInterval(t);
  }, [autoRefresh, fetchMetrics]);

  // ---------- activity log ----------
  const [summary, setSummary] = useState({ today_actions: 0, today_active_users: 0, today_errors: 0 });
  const [filterOptions, setFilterOptions] = useState({ actions: [], portals: [], methods: [], statuses: ['all','2xx','4xx','5xx'] });
  const [filters, setFilters] = useState({ employee: '', action: 'all', portal: 'all', method: 'all', status: 'all', from_date: '', to_date: '' });
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const [activity, setActivity] = useState({ total: 0, rows: [] });
  const [activityLoading, setActivityLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/apm/activity/summary`, { headers: { Authorization: `Bearer ${token}` } });
      setSummary(res.data);
    } catch (err) { /* silent */ }
  }, []);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/apm/activity/filters`, { headers: { Authorization: `Bearer ${token}` } });
      setFilterOptions(res.data);
    } catch (err) { /* silent */ }
  }, []);

  const fetchActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v !== 'all') params.append(k, v);
      });
      params.append('page', page);
      params.append('page_size', PAGE_SIZE);
      const res = await axios.get(`${API}/apm/activity?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActivity(res.data);
    } catch (err) {
      toast.error('Failed to load activity log');
    } finally {
      setActivityLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { fetchSummary(); fetchFilterOptions(); }, [fetchSummary, fetchFilterOptions]);
  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  // ---------- alert test ----------
  const [sendingTest, setSendingTest] = useState(false);
  const sendTestAlert = async () => {
    setSendingTest(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/apm/alerts/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data?.ok ? `Test alert sent to ${res.data.recipient}` : 'Alert send failed (check SMTP)');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Alert failed');
    } finally {
      setSendingTest(false);
    }
  };

  const downloadCsv = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== 'all') params.append(k, v);
    });
    // Build a temporary signed URL by injecting bearer via fetch + blob.
    fetch(`${API}/apm/activity/export.csv?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(async (r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity-log-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }).catch(() => toast.error('CSV export failed'));
  };

  const breaches = metrics?.breaches || [];
  const sys = metrics?.system || {};
  const dbm = metrics?.database || {};
  const req = metrics?.requests || {};
  const summarym = metrics?.summary || {};
  const endpoints = metrics?.endpoints || [];
  const slow = metrics?.slow_queries || [];

  const totalPages = useMemo(() => Math.max(1, Math.ceil((activity.total || 0) / PAGE_SIZE)), [activity.total]);

  return (
    <div className="space-y-6" data-testid="apm-portal">
      {/* Breach banner */}
      {breaches.length > 0 && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4 flex items-start gap-3" data-testid="apm-breach-banner">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <div className="font-semibold text-red-900">{breaches.length} metric(s) above threshold</div>
            <ul className="text-sm text-red-800 mt-1 list-disc ml-5">
              {breaches.map(b => (
                <li key={b.metric}>{b.label} — current {Number(b.value).toFixed(2)} (limit {b.threshold})</li>
              ))}
            </ul>
            <div className="text-xs text-red-700 mt-1">An alert email is sent to {metrics?.alert_recipient} (30-min cooldown per metric).</div>
          </div>
        </div>
      )}

      {/* Top action bar */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Activity className="w-5 h-5 text-emerald-600" /> Application Performance Monitor</CardTitle>
            <CardDescription>
              Live system & request metrics. Alerts go to <strong>{metrics?.alert_recipient || 'ess@greybulltechnologies.com'}</strong> when any metric exceeds its threshold.
              Auto-refreshes every 30s.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchMetrics} data-testid="refresh-metrics-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${metricsLoading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button variant={autoRefresh ? 'default' : 'outline'} size="sm" onClick={() => setAutoRefresh(v => !v)} data-testid="toggle-autorefresh">
              Auto: {autoRefresh ? 'ON' : 'OFF'}
            </Button>
            <Button variant="outline" size="sm" onClick={sendTestAlert} disabled={sendingTest} data-testid="send-test-alert-btn">
              <Bell className="w-4 h-4 mr-1" /> {sendingTest ? 'Sending…' : 'Test alert'}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* System gauges */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Gauge value={summarym.cpu_percent} label="CPU" icon={Cpu} accent="indigo" />
        <Gauge value={summarym.memory_percent} label="Memory" icon={MemoryStick} accent="purple" />
        <Gauge value={summarym.disk_percent} label="Disk" icon={HardDrive} accent="cyan" />
        <Gauge value={summarym.error_rate_percent} label="API error rate" icon={AlertTriangle} accent="emerald" dangerAt={5} />
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Active users (15 min)" value={summarym.active_users_15m ?? 0} sub="last 15 min" icon={Users} />
        <StatCard label="p95 latency" value={`${summarym.p95_latency_ms ?? 0} ms`} sub="last 15 min" icon={Clock} danger={(summarym.p95_latency_ms ?? 0) > 2000} />
        <StatCard label="Failed logins" value={summarym.failed_login_rate ?? 0} sub="last 15 min" icon={AlertTriangle} danger={(summarym.failed_login_rate ?? 0) > 10} />
        <StatCard label="Today's actions" value={summary.today_actions} sub={`${summary.today_active_users} active users · ${summary.today_errors} errors`} icon={Activity} />
      </div>

      {/* System + DB + Network */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">System</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <div>CPU cores: <strong>{sys.cpu_count}</strong></div>
            <div>Memory: <strong>{sys.memory_used_mb} / {sys.memory_total_mb} MB</strong></div>
            <div>Disk: <strong>{sys.disk_used_gb} / {sys.disk_total_gb} GB</strong></div>
            <div>Load avg: <strong>{sys.load_1m?.toFixed?.(2) ?? '—'} / {sys.load_5m?.toFixed?.(2) ?? '—'} / {sys.load_15m?.toFixed?.(2) ?? '—'}</strong></div>
            <div>Uptime: <strong>{Math.round((sys.uptime_seconds || 0) / 3600)} h</strong></div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Database</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <div>DB: <strong>{dbm.db_name}</strong></div>
            <div>Data size: <strong>{dbm.size_mb} MB</strong></div>
            <div>Storage size: <strong>{dbm.storage_size_mb} MB</strong></div>
            <div>Collections: <strong>{dbm.collections}</strong> · Documents: <strong>{dbm.objects?.toLocaleString?.() || dbm.objects}</strong></div>
            <div>Connections: <strong>{dbm.connections_current} / {dbm.connections_available} avail.</strong></div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Network className="w-4 h-4" /> Network</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <div>Outbound: <strong>{sys.network?.sent_kbps ?? '—'} kbps</strong></div>
            <div>Inbound: <strong>{sys.network?.recv_kbps ?? '—'} kbps</strong></div>
            <div>Total sent: <strong>{sys.network?.total_sent_mb ?? '—'} MB</strong></div>
            <div>Total received: <strong>{sys.network?.total_recv_mb ?? '—'} MB</strong></div>
            <div>Window: <strong>{req.window_minutes ?? 15} min</strong> · Reqs: <strong>{req.total_requests ?? 0}</strong></div>
          </CardContent>
        </Card>
      </div>

      {/* Endpoint latency breakdown */}
      <Card>
        <CardHeader><CardTitle className="text-base">Top endpoints by avg latency (last 60 min)</CardTitle></CardHeader>
        <CardContent>
          {endpoints.length === 0 ? (
            <p className="text-sm text-gray-500">No traffic yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Endpoint</TableHead>
                  <TableHead>Calls</TableHead>
                  <TableHead>Avg ms</TableHead>
                  <TableHead>Max ms</TableHead>
                  <TableHead>5xx</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {endpoints.map((e, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono text-xs">{e.method} {e.path}</TableCell>
                    <TableCell>{e.count}</TableCell>
                    <TableCell className={e.avg_ms > 2000 ? 'text-red-700 font-semibold' : ''}>{e.avg_ms}</TableCell>
                    <TableCell className={e.max_ms > 2000 ? 'text-red-700 font-semibold' : ''}>{e.max_ms}</TableCell>
                    <TableCell>{e.errors > 0 ? <Badge variant="destructive">{e.errors}</Badge> : 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Slow queries */}
      <Card>
        <CardHeader><CardTitle className="text-base">Slow requests (&gt; 1s, last 60 min)</CardTitle></CardHeader>
        <CardContent>
          {slow.length === 0 ? (
            <p className="text-sm text-gray-500">No slow requests. 🎉</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Endpoint</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {slow.map((s, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs">{s.created_at ? new Date(s.created_at).toLocaleTimeString() : '—'}</TableCell>
                    <TableCell className="text-sm">{s.employee_name || s.email}</TableCell>
                    <TableCell className="font-mono text-xs">{s.method} {s.path}</TableCell>
                    <TableCell className="font-semibold text-red-700">{s.duration_ms} ms</TableCell>
                    <TableCell>{s.status_code}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Activity log */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-base">Activity log</CardTitle>
            <CardDescription>
              Auto-deleted after 5 days. Showing {activity.rows.length} of {activity.total.toLocaleString()} rows matching filters.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={downloadCsv} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
            <div className="md:col-span-2">
              <Label className="text-xs">Employee (name/email)</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
                <Input
                  className="pl-8"
                  placeholder="nidhi.k@…"
                  value={filters.employee}
                  onChange={(e) => { setFilters(f => ({ ...f, employee: e.target.value })); setPage(1); }}
                  data-testid="filter-employee"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Action</Label>
              <Select value={filters.action} onValueChange={(v) => { setFilters(f => ({ ...f, action: v })); setPage(1); }}>
                <SelectTrigger data-testid="filter-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {filterOptions.actions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Portal</Label>
              <Select value={filters.portal} onValueChange={(v) => { setFilters(f => ({ ...f, portal: v })); setPage(1); }}>
                <SelectTrigger data-testid="filter-portal"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {filterOptions.portals.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={filters.from_date} onChange={(e) => { setFilters(f => ({ ...f, from_date: e.target.value })); setPage(1); }} />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={filters.to_date} onChange={(e) => { setFilters(f => ({ ...f, to_date: e.target.value })); setPage(1); }} />
            </div>
          </div>

          {activityLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : activity.rows.length === 0 ? (
            <p className="text-sm text-gray-500">No matching activity.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="activity-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Endpoint</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>IP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activity.rows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs">{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</TableCell>
                      <TableCell className="text-sm">
                        <div>{r.employee_name || '—'}</div>
                        <div className="text-xs text-gray-500">{r.email}</div>
                      </TableCell>
                      <TableCell><Badge variant="secondary">{r.action}</Badge></TableCell>
                      <TableCell className="font-mono text-xs">{r.method} {r.path}</TableCell>
                      <TableCell>
                        <Badge variant={r.status_code >= 500 ? 'destructive' : r.status_code >= 400 ? 'outline' : 'secondary'}>
                          {r.status_code}
                        </Badge>
                      </TableCell>
                      <TableCell className={r.duration_ms > 1000 ? 'text-red-700 font-semibold text-xs' : 'text-xs'}>
                        {r.duration_ms} ms
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">{r.ip}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="flex items-center justify-between mt-4 text-sm">
            <div className="text-gray-500">Page {page} of {totalPages}</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default APMPortal;
